import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  Search, 
  Bell, 
  Settings, 
  User, 
  LogOut,
  Home,
  Users,
  Building,
  BarChart3,
  Rocket,
  ChevronDown,
  Plus,
  MessageSquare,
  FileText,
  Eye,
  Building2,
  Zap,
  Sparkles,
  Phone,
  Mic,
  Shield,
  HelpCircle,
  Activity,
  GitBranch
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button/Button';
import { cn } from '../../utils/cn';

/**
 * Simple navigation items without heavy sectioning
 */
interface NavigationItem {
  id: string;
  label: string;
  icon?: React.ElementType;
  href: string;
  badge?: string | number;
  description?: string;
  submenu?: NavigationItem[];
}

const navigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Home',
    icon: Home,
    href: '/dashboard',
    description: 'Dashboard overview',
  },
  {
    id: 'onboarding',
    label: 'Smart Setup',
    icon: Sparkles,
    href: '/onboarding',
    description: 'AI-guided campaign setup',
  },
  {
    id: 'search-people',
    label: 'People',
    icon: Users,
    href: '/search/people',
    description: 'Find prospects and contacts',
  },
  {
    id: 'search-companies',
    label: 'Companies',
    icon: Building,
    href: '/search/companies',
    description: 'Search for target companies',
  },
  {
    id: 'customers',
    label: 'Lists',
    icon: FileText,
    href: '/customers',
    badge: '2.4k',
    description: 'Manage your customer lists',
  },
  {
    id: 'sequences',
    label: 'Sequences',
    icon: Zap,
    href: '/sequences',
    badge: 3,
    description: 'Multi-channel outreach sequences',
  },
  {
    id: 'workflows',
    label: 'Workflows',
    icon: Zap,
    href: '/workflows',
    description: 'Automated workflow engine',
  },
  {
    id: 'campaigns',
    label: 'Campaigns',
    icon: Rocket,
    href: '/campaigns',
    badge: 12,
    description: 'Active campaigns and results',
  },
  {
    id: 'conversations',
    label: 'Conversations',
    icon: MessageSquare,
    href: '/conversations',
    badge: 7,
    description: 'Active conversations',
  },
  {
    id: 'sms-conversations',
    label: 'SMS Conversations',
    icon: Phone,
    href: '/sms/conversations',
    badge: 15,
    description: 'SMS conversation management',
  },
  {
    id: 'voicemail-campaigns',
    label: 'Voicemail Campaigns',
    icon: Mic,
    href: '/voicemail/campaigns',
    badge: 3,
    description: 'Ringless voicemail campaigns',
  },
  {
    id: 'contracts',
    label: 'Deals',
    icon: Building2,
    href: '/contracts',
    badge: 5,
    description: 'Track deals and contracts',
  },
  {
    id: 'templates',
    label: 'Templates',
    icon: FileText,
    href: '/templates',
    description: 'Email, SMS, LinkedIn templates',
  },
  {
    id: 'review',
    label: 'Tasks',
    icon: Eye,
    href: '/review',
    badge: 5,
    description: 'Review queue and tasks',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    href: '/analytics',
    description: 'Performance analytics',
    submenu: [
      {
        id: 'analytics-overview',
        label: 'Overview',
        icon: BarChart3,
        href: '/analytics',
        description: 'General analytics dashboard'
      },
      {
        id: 'analytics-sms',
        label: 'SMS Analytics',
        icon: MessageSquare,
        href: '/analytics/sms/demo-campaign',
        description: 'SMS campaign performance'
      },
      {
        id: 'analytics-voicemail',
        label: 'Voicemail Analytics',
        icon: Phone,
        href: '/analytics/voicemail/demo-campaign',
        description: 'Voicemail campaign performance'
      }
    ]
  },
  {
    id: 'traces',
    label: 'System Traces',
    icon: GitBranch,
    href: '/traces',
    description: 'System trace monitoring and debugging',
  },
  {
    id: 'costs',
    label: 'Costs',
    icon: BarChart3,
    href: '/costs',
    description: 'Cost management, budgets, and optimization',
  },
  {
    id: 'help',
    label: 'Help',
    icon: HelpCircle,
    href: '/help',
    description: 'Help and support center',
  },
  {
    id: 'compliance',
    label: 'Compliance',
    icon: Shield,
    href: '/compliance',
    description: 'TCPA compliance & opt-out management',
  },
];

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();

  const currentPath = location.pathname;

  // Enhanced active item detection
  const getActiveItemId = () => {
    if (currentPath.startsWith('/onboarding')) return 'onboarding';
    if (currentPath.startsWith('/search/companies')) return 'search-companies';
    if (currentPath.startsWith('/search/people')) return 'search-people';
    if (currentPath.startsWith('/search')) return 'search-companies';
    if (currentPath.startsWith('/customers')) return 'customers';
    if (currentPath.startsWith('/sequences')) return 'sequences';
    if (currentPath.startsWith('/campaigns')) return 'campaigns';
    if (currentPath.startsWith('/templates')) return 'templates';
    if (currentPath.startsWith('/review')) return 'review';
    if (currentPath.startsWith('/sms/conversations')) return 'sms-conversations';
    if (currentPath.startsWith('/voicemail/campaigns')) return 'voicemail-campaigns';
    if (currentPath.startsWith('/conversations')) return 'conversations';
    if (currentPath.startsWith('/contracts')) return 'contracts';
    if (currentPath.startsWith('/analytics')) return 'analytics';
    if (currentPath.startsWith('/traces')) return 'traces';
    if (currentPath.startsWith('/compliance')) return 'compliance';
    if (currentPath.startsWith('/settings')) return 'settings';
    
    const segments = currentPath.split('/').filter(Boolean);
    return segments[0] || 'dashboard';
  };
  
  const activeItemId = getActiveItemId();

  // Mouse hover handlers
  const handleMouseEnter = () => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(true);
    }, 150);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
      setIsProfileOpen(false);
    }, 300);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleItemClick = (item: NavigationItem) => {
    if (item.href) {
      navigate(item.href);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* Collapsed Sidebar - Always Visible */}
      <motion.div
        ref={sidebarRef}
        className="fixed left-0 top-0 h-screen w-16 bg-gradient-to-b from-neutral-950 via-neutral-900 to-neutral-950 backdrop-blur-xl border-r border-neutral-800/50 z-50 flex flex-col"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Collapsed Header with Logo */}
        <div className="p-3 border-b border-neutral-800/50">
          <div className="flex items-center justify-center">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Rocket className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        {/* Collapsed Navigation Icons */}
        <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
          <div className="space-y-1 px-2">
            {navigationItems.map((item) => {
              const isActive = activeItemId === item.id || currentPath.startsWith(item.href);
              
              return (
                <motion.button
                  key={item.id}
                  className={cn(
                    'w-full flex items-center justify-center p-2.5 rounded-lg transition-all duration-200 group relative',
                    isActive 
                      ? 'bg-blue-500/90 text-white shadow-lg shadow-blue-500/20' 
                      : 'text-neutral-400 hover:text-white hover:bg-neutral-800/60'
                  )}
                  onClick={() => handleItemClick(item)}
                  whileTap={{ scale: 0.98 }}
                >
                  {item.icon && <item.icon className="w-5 h-5 flex-shrink-0" />}
                  
                  {/* Badge for collapsed state */}
                  {item.badge && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                      {item.badge}
                    </span>
                  )}
                  
                  {/* Tooltip */}
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-2 bg-neutral-800 text-white text-sm rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 border border-neutral-700">
                    <div className="font-medium">{item.label}</div>
                    {item.description && (
                      <div className="text-xs text-neutral-300 mt-1">
                        {item.description}
                      </div>
                    )}
                    {item.badge && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-neutral-600 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </nav>

        {/* Settings Icon */}
        <div className="p-2 border-t border-neutral-800/50">
          <motion.button
            className={cn(
              'w-full flex items-center justify-center p-2.5 rounded-lg transition-all duration-200 group relative',
              'text-neutral-400 hover:text-white hover:bg-neutral-800/60'
            )}
            onClick={() => navigate('/settings')}
            whileTap={{ scale: 0.98 }}
          >
            <Settings className="w-5 h-5 flex-shrink-0" />
            
            {/* Tooltip */}
            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-3 py-2 bg-neutral-800 text-white text-sm rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 border border-neutral-700">
              <div className="font-medium">Admin Settings</div>
            </div>
          </motion.button>
        </div>

        {/* Collapsed User Profile */}
        <div className="p-2">
          <div className="flex items-center justify-center">
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-medium">
                  {user?.firstName?.charAt(0) || 'U'}{user?.lastName?.charAt(0) || ''}
                </span>
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-neutral-950 rounded-full"></div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Expanded Sidebar - Slides In */}
      <AnimatePresence>
        {isHovering && (
          <motion.div
            className="fixed left-0 top-0 h-screen w-64 bg-gradient-to-b from-neutral-950 via-neutral-900 to-neutral-950 backdrop-blur-xl border-r border-neutral-800/50 shadow-2xl z-60 flex flex-col"
            initial={{ x: -256, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -256, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {/* Expanded Header with Logo and Search */}
            <div className="p-4 border-b border-neutral-800/50">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Rocket className="w-5 h-5 text-white" />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                    Bombard
                  </span>
                  <span className="text-xs text-neutral-400">
                    Sales Platform
                  </span>
                </div>
              </div>

              {/* Quick search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Quick search"
                  className="w-full pl-10 pr-12 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all text-sm"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <kbd className="px-2 py-0.5 text-xs font-semibold text-neutral-400 bg-neutral-700 border border-neutral-600 rounded">
                    ⌘K
                  </kbd>
                </div>
              </div>
            </div>

            {/* Expanded Navigation */}
            <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
              <div className="space-y-1 px-3">
                {navigationItems.map((item) => {
                  const isActive = activeItemId === item.id || currentPath.startsWith(item.href);
                  
                  return (
                    <motion.button
                      key={item.id}
                      className={cn(
                        'w-full flex items-center text-left transition-all duration-200',
                        'hover:bg-neutral-800/60 focus:outline-none focus:bg-neutral-800/60',
                        'group relative rounded-lg px-3 py-2.5',
                        isActive 
                          ? 'bg-blue-500/90 text-white shadow-lg shadow-blue-500/20' 
                          : 'text-neutral-400 hover:text-white'
                      )}
                      onClick={() => handleItemClick(item)}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center space-x-3">
                        {item.icon && (
                          <div className={`p-2 rounded-lg ${
                            isActive 
                              ? 'bg-blue-500/20 text-blue-400' 
                              : 'bg-gray-700/50 text-gray-400 group-hover:bg-gray-600/50 group-hover:text-gray-300'
                          }`}>
                            <item.icon className="w-5 h-5" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className={`font-medium ${
                            isActive ? 'text-blue-400' : 'text-gray-300 group-hover:text-white'
                          }`}>
                          {item.label}
                          </div>
                          {item.description && (
                            <div className="text-xs text-gray-500 group-hover:text-gray-400">
                              {item.description}
                            </div>
                          )}
                        </div>
                        {item.badge && (
                          <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </nav>

            {/* Settings */}
            <div className="px-3 py-2 border-t border-neutral-800/50">
              <motion.button
                className={cn(
                  'w-full flex items-center text-left transition-all duration-200',
                  'hover:bg-neutral-800/60 focus:outline-none focus:bg-neutral-800/60',
                  'group relative rounded-lg px-3 py-2.5',
                  'text-neutral-400 hover:text-white'
                )}
                onClick={() => navigate('/settings')}
                whileTap={{ scale: 0.98 }}
              >
                <Settings className="w-5 h-5 flex-shrink-0 mr-3" />
                <span className="text-sm font-medium">Admin Settings</span>
                <ChevronDown className="w-4 h-4 ml-auto" />
              </motion.button>
            </div>

            {/* Expanded User Profile Footer */}
            <div className="p-3">
              <div className="relative">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="w-full flex items-center space-x-3 p-3 hover:bg-neutral-800/50 rounded-lg transition-colors"
                >
                  <div className="relative">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-medium">
                        {user?.firstName?.charAt(0) || 'U'}{user?.lastName?.charAt(0) || ''}
                      </span>
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-neutral-950 rounded-full"></div>
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-neutral-400 truncate">
                      {user?.email}
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                </button>

                <AnimatePresence>
                  {isProfileOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute left-0 right-0 bottom-full mb-2 bg-neutral-900/95 backdrop-blur-xl border border-neutral-800 rounded-xl shadow-2xl overflow-hidden z-50"
                    >
                      <div className="p-2">
                        <button 
                          onClick={() => navigate('/settings')}
                          className="w-full flex items-center space-x-3 px-3 py-2 text-neutral-300 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors"
                        >
                          <Settings className="w-4 h-4" />
                          <span className="text-sm">Settings</span>
                        </button>
                        <button 
                          onClick={handleLogout}
                          className="w-full flex items-center space-x-3 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          <span className="text-sm">Sign out</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Upgrade CTA */}
            <div className="p-3">
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg p-3 text-center">
                <p className="text-sm font-semibold text-black">Upgrade</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar; 